/**
 * @fileoverview A wrapper component that provides fixed height calculations for AG Grid implementations
 * Handles responsive sizing and various grid layout configurations
 * @module AgGridFixedHeightWrapper
 */

import * as React from 'react';
import { FunctionComponent } from 'react';
import { useWindowSize, useContentHeight } from '../utils/hooks';
import { Box } from 'basecamp';

/**
 * Props interface for the AG Grid Fixed Height Wrapper component
 * @interface Props
 * @property {string} [classNames] - Optional CSS class names to apply to the wrapper
 * @property {boolean} [isMultiGrid] - Flag indicating if this is a multi-grid layout
 * @property {boolean} [hasFooter] - Flag indicating if the grid has a footer component
 * @property {boolean} [isTabbedGrid] - Flag indicating if this is a tabbed grid layout
 * @property {string} [width] - Optional width override for the grid wrapper
 * @property {number} [heightOffset] - Optional height offset for content calculations
 */
type Props = {
	classNames?: string;
	dataTest?: string;
	isMultiGrid?: boolean;
	hasFooter?: boolean;
	isTabbedGrid?: boolean;
	width?: string;
	heightOffset?: number;
};

/**
 * A wrapper component that provides fixed height calculations for AG Grid
 * Handles responsive sizing and various grid layout configurations
 *
 * @component
 * @param {Props} props - Component props
 * @param {boolean} props.isMultiGrid - Flag for multi-grid layout
 * @param {boolean} props.isTabbedGrid - Flag for tabbed grid layout
 * @param {ReactNode} props.children - Child components to render within the wrapper
 * @param {boolean} props.hasFooter - Flag indicating presence of footer
 * @param {string} props.width - Width override
 * @param {string} props.classNames - Additional CSS classes
 * @param {number} props.heightOffset - Height offset for calculations (defaults to 176)
 * @returns {JSX.Element} The rendered AG Grid wrapper component
 */
const AgGridFixedHeightWrapper: FunctionComponent<Props> = ({
	isMultiGrid,
	isTabbedGrid,
	children,
	hasFooter,
	width,
	classNames,
	dataTest,
	heightOffset = 176,
}) => {
	/**
	 * Get the calculated content height using the useContentHeight hook
	 * @constant {number} contentHeight - Calculated height for the grid container
	 */
	const { contentHeight } = useContentHeight(heightOffset);

	return (
		<Box
			className={
				isMultiGrid ? `ag-theme-alps is-multi-grid ${classNames}` : `ag-theme-alps  ${classNames}`
			}
			dataTest={dataTest}
			height={contentHeight}
			width={width ? width : 1}
		>
			{children}
		</Box>
	);
};

export default AgGridFixedHeightWrapper;
